import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosClient from "../../../../api-client/AxiosClient";
import "./ProcessSettings.css";

const DEPTH_COLORS = ["#1E3569", "#2563EB", "#60a5fa", "#93c5fd"];
const depthColor = (d) => DEPTH_COLORS[Math.min(d, DEPTH_COLORS.length - 1)];

let _tempCounter = 1;
const newTempId = () => `__new_${_tempCounter++}`;

const normalizeDateForInput = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "number" || /^[0-9]+$/.test(String(value).trim())) {
    const parsed = new Date(Number(value));
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}-${String(parsed.getUTCDate()).padStart(2, "0")}`;
  }
  const str = String(value).trim();
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  const hasTzDesignator = /Z$|[+-]\d{2}:?\d{2}$/.test(str);
  if (dateOnlyMatch && !hasTzDesignator) {
    return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
  }
  const parsed = new Date(str.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, "0")}-${String(parsed.getUTCDate()).padStart(2, "0")}`;
};

const normaliseApiTree = (nodes) =>
  (nodes || []).map((n) => ({
    taskIterationId: n.taskIterationId,
    tempId: n.taskIterationId,
    name: n.name || "",
    startDate: normalizeDateForInput(n.startDate),
    endDate: normalizeDateForInput(n.endDate),
    displayOrder: n.displayOrder ?? null,
    parentTaskIterationId: n.parentTaskIterationId ?? null,
    _parentTempId: n.parentTaskIterationId ?? null,
    taskCount: n.taskCount || 0,
    _isNew: false,
    _isDirty: false,
    expanded: true,
    children: normaliseApiTree(n.children || []),
  }));

const cloneTree = (nodes) =>
  nodes.map((n) => ({ ...n, children: cloneTree(n.children) }));

const findNode = (tempId, nodes) => {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].tempId === tempId) return [nodes[i], nodes, i];
    const found = findNode(tempId, nodes[i].children);
    if (found) return found;
  }
  return null;
};

const countDescendants = (node) => {
  if (!node.children?.length) return 0;
  return node.children.reduce((acc, c) => acc + 1 + countDescendants(c), 0);
};

const walkBFS = (nodes, fn) => {
  const queue = [...nodes];
  while (queue.length) {
    const n = queue.shift();
    fn(n);
    if (n.children?.length) queue.push(...n.children);
  }
};

const selectStyle = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    width: "100%",
    fontSize: "13px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3B82F6" : "#E5E7EB",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.12)" : "none",
    "&:hover": { borderColor: "#93C5FD" },
    transition: "all 0.15s ease",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF", fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px", color: "#111827", fontWeight: 500 }),
  option: (base, state) => ({
    ...base,
    fontSize: "13px",
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#2563EB" : state.isFocused ? "#EFF6FF" : "white",
    color: state.isSelected ? "white" : "#111827",
    fontWeight: state.isSelected ? 600 : 400,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
    border: "1px solid #E5E7EB",
    zIndex: 9999,
  }),
  loadingMessage: (base) => ({ ...base, fontSize: "13px", color: "#6B7280", padding: "8px 12px" }),
  loadingIndicator: (base) => ({ ...base, color: "#9CA3AF" }),
};

// ─── Main Component ────────────────────────────────────────────────────────────
const ProcessSettings = () => {
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const [configOptions, setConfigOptions] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const [tree, setTree] = useState([]);
  const treeRef = useRef([]);                    // always current — avoids stale closure in saveSettings
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, node: null });
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const pendingDeletesRef = useRef([]);          // always current — avoids stale closure in saveSettings

  const configFetchInFlight = useRef({});

  // Keep refs in sync with state so saveSettings never reads a stale closure
  const setTreeSync = (val) => { treeRef.current = val; setTree(val); };
  const setPendingDeletesSync = (val) => { pendingDeletesRef.current = val; setPendingDeletes(val); };

  // ── Load Organizations ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchOrgs = async () => {
      setOrgsLoading(true);
      try {
        const res = await axiosClient.get("/api/Organization/GetUserOrganizations");
        const data = Array.isArray(res?.data) ? res.data : [];
        const options = data
          .map((o) => ({
            value: String(o?.organizationId ?? o?.organization_id ?? o?.id ?? ""),
            label: o?.organizationName ?? o?.organization_name ?? o?.name ?? "",
          }))
          .filter((o) => o.value);
        setOrganizations(options);
        if (options.length > 0) setSelectedOrg(options[0]);
      } catch {
        toast.error("Failed to load organizations.");
      } finally {
        setOrgsLoading(false);
      }
    };
    fetchOrgs();
  }, []);

  // ── Load Configs when Org changes → auto-select first config ──────────────
  useEffect(() => {
    if (!selectedOrg) {
      setConfigOptions([]);
      setSelectedConfig(null);
      return;
    }
    const orgId = selectedOrg.value;
    if (configFetchInFlight.current[orgId]) return;
    configFetchInFlight.current[orgId] = true;

    setConfigsLoading(true);
    setSelectedConfig(null);
    setConfigOptions([]);
    setTreeSync([]);
    setPendingDeletesSync([]);

    axiosClient
      .get(`/api/Organization/GetOrganizationConfigs?orgId=${orgId}`)
      .then((res) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        const options = data
          .map((c) => {
            const value = String(c?.organizationConfigId ?? c?.organization_config_id ?? c?.id ?? "");
            const source = c?.sourceName ?? c?.source_name ?? "";
            const target = c?.targetName ?? c?.target_name ?? "";
            const label =
              source && target
                ? `${source} -> ${target}`
                : (c?.domainName ?? c?.domain_name ?? value);
            return value ? { value, label } : null;
          })
          .filter(Boolean);
        setConfigOptions(options);
        if (options.length > 0) setSelectedConfig(options[0]);
      })
      .catch(() => toast.error("Failed to load organization configurations."))
      .finally(() => {
        setConfigsLoading(false);
        configFetchInFlight.current[orgId] = false;
      });
  }, [selectedOrg]);

  // ── Load tree for selected config ──────────────────────────────────────────
  // Full reset load: used when switching org/config or after save. Resets all state.
  const loadTree = useCallback(async (orgConfigId) => {
    if (!orgConfigId) return;

    setLoading(true);

    try {
      const res = await axiosClient.get(
        `/api/task/iterations/tree?orgConfigId=${orgConfigId}`
      );

      const freshTree = normaliseApiTree(res.data || []);

    // Clear pending deletes so hasPendingChanges resolves correctly after reload
    pendingDeletesRef.current = [];
    setPendingDeletes([]);

    // update ref first, then UI
    treeRef.current = freshTree;
    setTree(freshTree);

    } catch {
      toast.error("Failed to load iterations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedConfig) loadTree(selectedConfig.value);
  }, [selectedConfig, loadTree]);

  // ── Org change ─────────────────────────────────────────────────────────────
  const handleOrgChange = (opt) => {
    setSelectedOrg(opt);
    setSelectedConfig(null);
    setConfigOptions([]);
    setTreeSync([]);
    setPendingDeletesSync([]);
    if (opt) delete configFetchInFlight.current[opt.value];
  };

  // ── Config change ──────────────────────────────────────────────────────────
  const handleConfigChange = (opt) => {
    setSelectedConfig(opt);
    setPendingDeletes([]);
  };

  // ── Tree helpers ───────────────────────────────────────────────────────────
  const updateTree = (fn) => {
    setTree((prev) => {
      const next = fn(cloneTree(prev));
      treeRef.current = next;
      return [...next];
    });
  };

  const toggleExpand = (tempId) => {
    updateTree((draft) => {
      const found = findNode(tempId, draft);
      if (found) found[0].expanded = !found[0].expanded;
      return draft;
    });
  };

  const handleFieldChange = (tempId, field, value) => {
    updateTree((draft) => {
      const found = findNode(tempId, draft);
      if (found) {
        found[0][field] = value;
        if (!found[0]._isNew) found[0]._isDirty = true;
      }
      return draft;
    });
  };

  const addTopLevel = () => {
    const node = {
      taskIterationId: null, tempId: newTempId(), name: "", startDate: "", endDate: "",
      displayOrder: null, parentTaskIterationId: null, _parentTempId: null,
      taskCount: 0, _isNew: true, _isDirty: false, expanded: true, children: [],
    };
    setTree((prev) => {
      const next = [...prev, node];
      treeRef.current = next;
      return next;
    });
    setTimeout(() => {
      document.querySelector(`[data-tempid="${node.tempId}"] .ps-name-input`)?.focus();
    }, 60);
  };

  const addChild = (parentTempId) => {
    const node = {
      taskIterationId: null, tempId: newTempId(), name: "", startDate: "", endDate: "",
      displayOrder: null, parentTaskIterationId: null, _parentTempId: parentTempId,
      taskCount: 0, _isNew: true, _isDirty: false, expanded: true, children: [],
    };
    updateTree((draft) => {
      const found = findNode(parentTempId, draft);
      if (found) {
        if (found[0].taskIterationId) node.parentTaskIterationId = found[0].taskIterationId;
        found[0].children.push(node);
        found[0].expanded = true;
      }
      return draft;
    });
    setTimeout(() => {
      document.querySelector(`[data-tempid="${node.tempId}"] .ps-name-input`)?.focus();
    }, 60);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const collectDeleteIds = (node, ids) => {
    if (node.taskIterationId != null) ids.push(node.taskIterationId);
    node.children.forEach((c) => collectDeleteIds(c, ids));
  };

  const removeNodeByTempId = (nodes, tempId, deleteIds) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].tempId === tempId) {
        if (!nodes[i]._isNew) collectDeleteIds(nodes[i], deleteIds);
        nodes.splice(i, 1);
        return true;
      }
      if (removeNodeByTempId(nodes[i].children, tempId, deleteIds)) return true;
    }
    return false;
  };

  const promptDelete = (node) => setDeleteModal({ open: true, node });
  const cancelDelete = () => setDeleteModal({ open: false, node: null });

  const confirmDelete = () => {
    const nodeToDelete = deleteModal.node;
    setDeleteModal({ open: false, node: null });
    if (!nodeToDelete) return;

    // Read from ref — guaranteed current even if React hasn't re-rendered yet
    const idsToDelete = [];
    if (!nodeToDelete._isNew) {
      const found = findNode(nodeToDelete.tempId, treeRef.current);
      if (found) collectDeleteIds(found[0], idsToDelete);
    }

    updateTree((draft) => {
      removeNodeByTempId(draft, nodeToDelete.tempId, []);
      return draft;
    });

    if (idsToDelete.length > 0) {
      const next = Array.from(new Set([...pendingDeletesRef.current, ...idsToDelete]));
      setPendingDeletesSync(next);
    }
  };

  // ── Save Settings ──────────────────────────────────────────────────────────
  const saveSettings = async () => {
    if (!selectedConfig) {
      toast.error("Please select an Organization Configuration before saving.");
      return;
    }

    // Read from refs — always current regardless of React render timing
    const configId = selectedConfig.value;
    const deletesToProcess = [...new Set(pendingDeletesRef.current)];

    setSaving(true);
    try {
      // ── 1. Validate ──────────────────────────────────────────────────────
      const ordered = [];
      walkBFS(treeRef.current, (n) => ordered.push(n));

      for (const node of ordered) {
        if (!(node.name || "").trim()) {
          toast.error("Iteration name is required");
          setSaving(false);
          return;
        }
        if (node.startDate && node.endDate && new Date(node.startDate) > new Date(node.endDate)) {
          toast.error(`${node.name || "Iteration"}: End Date must be after Start Date`);
          setSaving(false);
          return;
        }
      }

      // ── 2. Delete queued iterations ──────────────────────────────────────
      for (const id of deletesToProcess) {
        try { await axiosClient.delete(`/api/task/iterations/${id}`); } catch { /* ignore 404 */ }
      }

      // ── 3. Save new / dirty iterations ──────────────────────────────────
      const idMap = {};
      walkBFS(treeRef.current, (n) => { if (n.taskIterationId) idMap[n.tempId] = n.taskIterationId; });

      const formatDateForApi = (d) => {
        if (!d) return null;
        const value = typeof d === "number" ? String(d) : String(d).trim();
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toISOString();
        const parsed = /^[0-9]+$/.test(value) ? new Date(+value) : new Date(value.replace(" ", "T"));
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
      };

      for (const node of ordered) {
        let resolvedParentId = null;
        if (node._parentTempId) {
          resolvedParentId = idMap[node._parentTempId] ?? node.parentTaskIterationId ?? null;
        } else if (node.parentTaskIterationId) {
          resolvedParentId = node.parentTaskIterationId;
        }

        const payload = {
          name: node.name.trim(),
          isActive: true,
          startDate: formatDateForApi(node.startDate),
          endDate: formatDateForApi(node.endDate),
          displayOrder: node.displayOrder === null ? null : Number(node.displayOrder),
          parentTaskIterationId: resolvedParentId || null,
          organizationConfigId: configId,
        };

        if (node._isNew) {
          const res = await axiosClient.post("/api/task/iterations", payload);
          const newId = res.data?.taskIterationId;
          if (newId) idMap[node.tempId] = newId;
        } else if (node._isDirty) {
          await axiosClient.put(`/api/task/iterations/${node.taskIterationId}`, payload);
        }
      }

      // ── 4. Clear pending deletes FIRST, then reload fresh tree from server ──
      // pendingDeletes must be cleared before loadTree so that hasPendingChanges
      // correctly computes false as soon as the fresh tree lands in state.
      setPendingDeletesSync([]);

      await loadTree(configId);

      setSaving(false);

      toast.success("Settings saved successfully.");
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data || null;
      toast.error(serverMsg || err?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (selectedConfig) loadTree(selectedConfig.value);
    toast.info("Changes discarded.");
  };

  const hasPendingChanges = useMemo(() => {
    if (pendingDeletes.length > 0) return true;
    let dirty = false;
    walkBFS(tree, (n) => { if (n._isNew || n._isDirty) dirty = true; });
    return dirty;
  }, [tree, pendingDeletes]);

  const iterSubText = "Add top-level iterations and nest child iterations within them.";

  return (
    <div className="ps-page">
      <div className="ps-page-title">Process Settings</div>
      <div className="ps-page-sub">
        Define project iterations and phases used across Task Management.
      </div>

      <div className="ps-scope-bar">
        <div className="ps-scope-field">
          <label className="ps-scope-label" htmlFor="ps-org-select">Organization</label>
          <Select
            inputId="ps-org-select"
            options={organizations}
            value={selectedOrg}
            onChange={handleOrgChange}
            isLoading={orgsLoading}
            placeholder="Select organization..."
            styles={selectStyle}
            isClearable={false}
          />
        </div>
        <div className="ps-scope-field">
          <label className="ps-scope-label" htmlFor="ps-config-select">Organization Configuration</label>
          <Select
            inputId="ps-config-select"
            options={configOptions}
            value={selectedConfig}
            onChange={handleConfigChange}
            isLoading={configsLoading}
            isDisabled={!selectedOrg || configsLoading}
            placeholder="Select configuration..."
            styles={selectStyle}
            isClearable={false}
          />
        </div>
      </div>

      <div className="ps-tab-bar">
        <div className="ps-tab ps-tab-active">Iterations</div>
      </div>

      <div className="ps-card">
        <div className="ps-card-toolbar">
          <div>
            <div className="ps-card-toolbar-title">Iterations</div>
            <div className="ps-card-toolbar-sub">{iterSubText}</div>
          </div>
          <button
            className="ps-btn ps-btn-primary"
            onClick={addTopLevel}
            disabled={saving || !selectedConfig}
          >
            + Add Iteration
          </button>
        </div>

        <div className="ps-header-row">
          <div>Name</div>
          <div>Start Date</div>
          <div>End Date</div>
          <div className="ps-col-right">Actions</div>
        </div>

        <div className="ps-tree">
          {!selectedConfig ? (
            <div className="ps-empty-state">
              <div className="ps-empty-icon">🏢</div>
              <p>Select an Organization and Configuration above to manage iterations.</p>
            </div>
          ) : loading ? (
            <div className="ps-empty-state">
              <div className="ps-empty-icon">⏳</div>
              <p>Loading iterations…</p>
            </div>
          ) : tree.length === 0 ? (
            <div className="ps-empty-state">
              <div className="ps-empty-icon">📅</div>
              <p>No iterations yet. Click <strong>+ Add Iteration</strong> to get started.</p>
            </div>
          ) : (
            tree.map((node) => (
              <IterationNode
                key={node.tempId}
                node={node}
                depth={0}
                onToggle={toggleExpand}
                onChange={handleFieldChange}
                onAddChild={addChild}
                onDelete={promptDelete}
                saving={saving}
              />
            ))
          )}
        </div>
      </div>

      <div className="ps-save-bar">
        <span className="ps-save-hint">
          {hasPendingChanges ? "⚠️ You have unsaved changes" : ""}
        </span>
        <button
          className="ps-btn ps-btn-secondary"
          onClick={discardChanges}
          disabled={saving || loading || !selectedConfig}
        >
          Discard Changes
        </button>
        <button
          className="ps-btn ps-btn-save"
          onClick={saveSettings}
          disabled={saving || loading || !hasPendingChanges || !selectedConfig}
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {deleteModal.open && deleteModal.node && (
        <DeleteModal
          node={deleteModal.node}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

// ─── IterationNode ─────────────────────────────────────────────────────────────
const IterationNode = ({ node, depth, onToggle, onChange, onAddChild, onDelete, saving }) => {
  const hasChildren = node.children?.length > 0;
  const color = depthColor(depth);
  return (
    <>
      <div
        className={["ps-iter-row", node._isNew ? "ps-row-new" : "", node._isDirty ? "ps-row-dirty" : ""].join(" ").trim()}
        data-tempid={node.tempId}
      >
        <div className="ps-name-cell">
          {Array.from({ length: depth }).map((_, i) => (
            <span key={`indent-${depth}-${i}`} className="ps-indent">
              {i === depth - 1 && (<><span className="ps-connector-h" /><span className="ps-connector-v" /></>)}
            </span>
          ))}
          <button
            className={`ps-expand-btn${hasChildren ? "" : " ps-expand-btn--hidden"}`}
            onClick={() => onToggle(node.tempId)}
            tabIndex={hasChildren ? 0 : -1}
            aria-label={node.expanded ? "Collapse" : "Expand"}
          >
            {node.expanded ? "▼" : "▶"}
          </button>
          <span className="ps-color-bar" style={{ background: color }} />
          <input
            className="ps-name-input"
            type="text"
            value={node.name}
            placeholder="Iteration name…"
            onChange={(e) => onChange(node.tempId, "name", e.target.value)}
            disabled={saving}
          />
          {node._isNew && <span className="ps-badge-new">New</span>}
        </div>
        <div>
          <input
            type="date"
            className={`ps-date-input${node.startDate ? "" : " ps-date-empty"}`}
            value={node.startDate || ""}
            onChange={(e) => onChange(node.tempId, "startDate", e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <input
            type="date"
            className={`ps-date-input${node.endDate ? "" : " ps-date-empty"}`}
            value={node.endDate || ""}
            onChange={(e) => onChange(node.tempId, "endDate", e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="ps-actions-cell">
          <button className="ps-btn-icon ps-btn-icon--add" title="Add child iteration" onClick={() => onAddChild(node.tempId)} disabled={saving}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button className="ps-btn-icon ps-btn-icon--delete" title="Delete iteration" onClick={() => onDelete(node)} disabled={saving}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        </div>
      </div>
      {node.expanded && hasChildren && node.children.map((child) => (
        <IterationNode key={child.tempId} node={child} depth={depth + 1}
          onToggle={onToggle} onChange={onChange} onAddChild={onAddChild} onDelete={onDelete} saving={saving} />
      ))}
    </>
  );
};

IterationNode.propTypes = {
  node: PropTypes.shape({
    tempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    displayOrder: PropTypes.number,
    parentTaskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _parentTempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskCount: PropTypes.number,
    _isNew: PropTypes.bool.isRequired,
    _isDirty: PropTypes.bool.isRequired,
    expanded: PropTypes.bool.isRequired,
    children: PropTypes.array.isRequired,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onAddChild: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

const DeleteModal = ({ node, onConfirm, onCancel }) => {
  const childCount = countDescendants(node);
  return (
    <div className="ps-modal-overlay" role="button" tabIndex={0}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <div className="ps-modal">
        <div className="ps-modal-header">
          <span className="ps-modal-title">Delete Iteration</span>
          <button className="ps-btn-icon" onClick={onCancel} aria-label="Close">✕</button>
        </div>
        <div className="ps-modal-body">
          <p>Are you sure you want to delete <strong>{node.name || "this iteration"}</strong>?</p>
          {childCount > 0 && (
            <p className="ps-modal-warn">
              ⚠️ This will also delete {childCount} child iteration{childCount > 1 ? "s" : ""} inside it.
            </p>
          )}
        </div>
        <div className="ps-modal-footer">
          <button className="ps-btn ps-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="ps-btn ps-btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
};

DeleteModal.propTypes = {
  node: PropTypes.shape({
    tempId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskIterationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    children: PropTypes.array.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ProcessSettings;
